import * as React from "react"
import Image from "next/image"
import { ShoppingCart } from "lucide-react"
import { UseFormReturn } from "react-hook-form"

import { Category, CustomizeItem } from "@/lib/types"
import { formatCurrency, getPlaceHolderDishImage } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { ZoomableImage } from "@/components/ui/zoomable-image"
import { NumberIncrementer } from "@/components/number-incrementer"

import { formatItemDetails } from "../utils/cart-utils"
import { MenuItemCustomizeItemList } from "./menu-item-detail-customize-item"

interface MenuItemDetailContentProps {
  selectedItem: Category["items"][number]
  form: UseFormReturn<any>
  scrollAreaRef: React.RefObject<HTMLDivElement>
  hasSubmitted: boolean
  invalidCustomizations: number[]
  quantity: number
  setQuantity: (value: number) => void
  handleSubmit: (event: React.FormEvent) => void
  calculateTotal: () => number
  isSubmitting: boolean
}

export function MenuItemDetailContent({
  selectedItem,
  form,
  scrollAreaRef,
  hasSubmitted,
  invalidCustomizations,
  quantity,
  setQuantity,
  handleSubmit,
  calculateTotal,
  isSubmitting,
}: MenuItemDetailContentProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 w-full">
      <div className="w-full aspect-[16/7] lg:aspect-square overflow-hidden hidden lg:block">
        <ZoomableImage
          src={
            selectedItem?.item_image ||
            selectedItem?.item_image_compressed ||
            selectedItem?.item_image_compressed_web ||
            "/dish_thumbnail.webp"
          }
          alt={selectedItem?.item_name || ""}
          width={500}
          height={500}
          placeholder="blur"
          blurDataURL={getPlaceHolderDishImage()}
          className="w-auto xl:h-[492px] aspect-[16/7] lg:aspect-square max-h-full object-cover"
          quality={98}
          zoomScale={1.4}
        />
      </div>
      <form onSubmit={handleSubmit}>
        <Form {...form}>
          <div className="flex flex-col">
            <ScrollArea
              ref={scrollAreaRef}
              className="w-full h-[400px] scrollbar-hide"
            >
              <Image
                src={
                  selectedItem?.item_image ||
                  selectedItem?.item_image_compressed ||
                  selectedItem?.item_image_compressed_web ||
                  "/dish_thumbnail.webp"
                }
                alt={selectedItem?.item_name || ""}
                width={320}
                height={100}
                placeholder="blur"
                blurDataURL={getPlaceHolderDishImage()}
                className="w-full aspect-[16/5] object-cover block lg:hidden"
                quality={98}
              />
              <div className="flex flex-col gap-3 xl:pl-2 max-lg:px-5 max-lg:mt-5 font-be-vietnam pr-6">
                <div className="flex justify-between items-center sticky top-[-1] bg-white pb-2">
                  <h2 className="text-xl font-semibold pr-4 font-be-vietnam">
                    {selectedItem?.item_name}
                  </h2>
                  <span className="font-semibold text-base font-be-vietnam">
                    {selectedItem?.display_price}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={14} className="text-gray-500" />
                    <span className="text-xs text-gray-500">
                      {selectedItem?.order_count || 0} đã bán
                    </span>
                  </div>
                </div>

                <p className="text-gray-500 text-sm text-justify whitespace-normal overflow-wrap-anywhere hyphens-auto break-all">
                  {formatItemDetails(selectedItem?.item_details)}
                </p>

                <Textarea
                  name="note"
                  placeholder="Viết lời nhắn cho nhà hàng"
                  className="placeholder:text-gray-500 placeholder:uppercase placeholder:text-xs placeholder:font-semibold focus:outline-none focus-visible:ring-0"
                />

                <div className="mt-5 flex flex-col gap-4">
                  {selectedItem?.customize_item &&
                    selectedItem.customize_item.map((item: CustomizeItem) => (
                      <React.Fragment key={item.customize_id}>
                        <MenuItemCustomizeItemList
                          data={item}
                          register={form.register}
                          control={form.control}
                          hasSubmitted={hasSubmitted}
                          isRestoClosed={false}
                          isInvalid={invalidCustomizations.includes(
                            item.customize_id
                          )}
                        />
                      </React.Fragment>
                    ))}
                </div>
              </div>
            </ScrollArea>
            <div className="flex gap-3 mt-7 w-full border-t border-gray-200 pt-3 max-lg:pl-4 pr-3 lg:pr-4">
              <NumberIncrementer
                value={quantity}
                onIncrement={() => setQuantity(Math.min(quantity + 1, 99))}
                onDecrement={() => setQuantity(Math.max(quantity - 1, 0))}
              />

              <Button
                type="submit"
                className="flex-1 h-11 rounded-xl"
                variant={quantity > 0 ? "be" : "destructive"}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Đang xử lý..."
                  : quantity === 0
                    ? "Xoá khỏi giỏ hàng"
                    : `Thêm vào giỏ hàng - ${formatCurrency(calculateTotal())}`}
              </Button>
            </div>
          </div>
        </Form>
      </form>
    </div>
  )
}
